import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Button, Empty, Input, Select, Space, Table, Tag, Typography } from 'antd';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import pmvService, { type PmvProfileListItem } from '@/services/pmv.service';
import { useProjects } from '@/hooks/useProjects';
import { buildProjectIdToLabelMap, resolveProjectDisplayFromMap } from '@/utils/pmvDisplay';
import { connectivityLabel, connectivityTagColor } from '@/utils/pmvConnectedCopy';

const { Title, Text } = Typography;

const statusColor: Record<string, string> = {
  NOT_READY: 'default',
  AVAILABLE: 'success',
  OUT_OF_SERVICE: 'error',
  RESERVED: 'processing',
  UNDER_INSPECTION: 'warning',
  UNDER_MAINTENANCE: 'warning',
  BREAKDOWN: 'error',
  QUARANTINED: 'error',
  DEPLOYED: 'blue',
  IDLE: 'default',
  STANDBY: 'default',
};

const STATUS_OPTIONS = [
  { value: 'NOT_READY', label: 'Not ready' },
  { value: 'AVAILABLE', label: 'Available' },
  { value: 'RESERVED', label: 'Reserved' },
  { value: 'DEPLOYED', label: 'Deployed' },
  { value: 'UNDER_MAINTENANCE', label: 'Maintenance' },
  { value: 'BREAKDOWN', label: 'Breakdown' },
  { value: 'QUARANTINED', label: 'Compliance hold' },
  { value: 'UNDER_INSPECTION', label: 'Under inspection' },
  { value: 'OUT_OF_SERVICE', label: 'Out of service' },
  { value: 'IDLE', label: 'Idle' },
  { value: 'STANDBY', label: 'Standby' },
];

const PmvRegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [q, setQ] = useState('');
  const [operationalStatus, setOperationalStatus] = useState<string | undefined>(
    () => searchParams.get('status') || undefined
  );
  const { data: projects = [] } = useProjects();
  const projectLabels = useMemo(() => buildProjectIdToLabelMap(projects), [projects]);

  useEffect(() => {
    const st = searchParams.get('status');
    if (st) setOperationalStatus(st);
  }, [searchParams]);

  const listQ = useQuery({
    queryKey: ['pmv-register', page, pageSize, q, operationalStatus],
    queryFn: () =>
      pmvService.listProfiles({
        page,
        pageSize,
        q: q || undefined,
        operational_status: operationalStatus,
      }),
  });

  const connectedStatesQ = useQuery({
    queryKey: ['pmv-equipment-connectivity'],
    queryFn: () => pmvService.listEquipmentConnectivity(),
    staleTime: 60_000,
    retry: false,
  });

  const connectivityByProfile = useMemo(() => {
    const m = new Map<string, string | null>();
    for (const row of connectedStatesQ.data ?? []) {
      m.set(row.pmv_profile_id, row.connectivity);
    }
    return m;
  }, [connectedStatesQ.data]);

  const columns = useMemo(
    () => [
      {
        title: 'Unit',
        key: 'unit',
        width: 168,
        ellipsis: true,
        fixed: 'left' as const,
        render: (_: unknown, r: PmvProfileListItem) => (
          <Button
            type="link"
            style={{ padding: 0, height: 'auto', maxWidth: '100%' }}
            onClick={() => navigate(`/asset/pmv/${r.id}`)}
          >
            <Text ellipsis style={{ maxWidth: 148, color: 'inherit' }}>
              {r.fleet_number || r.asset_code}
            </Text>
          </Button>
        ),
      },
      {
        title: 'Asset code',
        dataIndex: 'asset_code',
        key: 'asset_code',
        width: 160,
        ellipsis: true,
      },
      {
        title: 'Name',
        dataIndex: 'asset_name',
        key: 'asset_name',
        width: 180,
        ellipsis: true,
      },
      {
        title: 'Make / Model',
        key: 'make',
        width: 140,
        ellipsis: true,
        render: (_: unknown, r: PmvProfileListItem) =>
          [r.manufacturer, r.model].filter(Boolean).join(' · ') || '—',
      },
      {
        title: 'Ownership',
        dataIndex: 'ownership_type',
        key: 'ownership_type',
        width: 120,
        ellipsis: true,
        render: (v: string) => (v ? v.replace(/_/g, ' ') : '—'),
      },
      {
        title: 'Allocation',
        dataIndex: 'current_allocation_status',
        key: 'current_allocation_status',
        width: 110,
        render: (v: string | null) => (v ? <Tag>{v.replace(/_/g, ' ')}</Tag> : '—'),
      },
      {
        title: 'Project',
        dataIndex: 'current_project_id',
        key: 'current_project_id',
        width: 160,
        ellipsis: true,
        render: (pid: string | null) => resolveProjectDisplayFromMap(projectLabels, pid),
      },
      {
        title: 'Expected release',
        dataIndex: 'expected_release',
        key: 'expected_release',
        width: 130,
        render: (v: string | null) => (v ? String(v).slice(0, 10) : '—'),
      },
      {
        title: 'Status',
        dataIndex: 'operational_status',
        key: 'operational_status',
        width: 150,
        render: (s: string) => <Tag color={statusColor[s] ?? 'default'}>{s.replace(/_/g, ' ')}</Tag>,
      },
      {
        title: 'Connectivity',
        key: 'connectivity',
        width: 120,
        render: (_: unknown, r: PmvProfileListItem) => {
          const band = connectivityByProfile.get(r.id);
          if (!band) return <Tag>Not connected</Tag>;
          return <Tag color={connectivityTagColor(band)}>{connectivityLabel(band)}</Tag>;
        },
      },
      {
        title: 'Criticality',
        dataIndex: 'criticality',
        key: 'criticality',
        width: 100,
        render: (v: string) => v || '—',
      },
    ],
    [navigate, projectLabels, connectivityByProfile]
  );

  const rows = listQ.data?.data ?? [];
  const total = listQ.data?.pagination?.total ?? 0;

  return (
    <WorkspaceLayout
      header={
        <EnterprisePageHeader
          documentLabel="Fleet Register"
          documentNumber=""
          statusTag={<Tag color="blue">Plant, Machinery & Vehicles</Tag>}
          primaryActions={
            <Space wrap>
              <Button onClick={() => navigate('/asset/pmv/control-tower')}>Command Center</Button>
              <Button onClick={() => navigate('/asset/pmv/hub')}>Hub</Button>
            </Space>
          }
        />
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Title level={5} style={{ margin: 0 }}>
          Plant, Machinery &amp; Vehicles
        </Title>
        <Space wrap>
          <Input.Search
            placeholder="Search code, name, serial, make…"
            allowClear
            style={{ width: 280 }}
            onSearch={(v) => {
              setQ(v);
              setPage(1);
            }}
          />
          <Select
            allowClear
            placeholder="Operational status"
            style={{ width: 200 }}
            value={operationalStatus}
            onChange={(v) => {
              setOperationalStatus(v);
              setPage(1);
            }}
            options={STATUS_OPTIONS}
          />
          <Button onClick={() => navigate('/asset/pmv/operations')}>Deployment</Button>
          <Button onClick={() => navigate('/asset/pmv/daily-logs')}>Field Operations</Button>
        </Space>
        <Table
          rowKey="id"
          loading={listQ.isLoading}
          columns={columns}
          dataSource={rows}
          locale={{
            emptyText: (
              <Empty
                description={
                  q || operationalStatus ? (
                    <Space direction="vertical" size={4}>
                      <Text>No equipment matches these filters.</Text>
                      <Button
                        type="link"
                        onClick={() => {
                          setQ('');
                          setOperationalStatus(undefined);
                          setPage(1);
                        }}
                      >
                        Clear filters
                      </Button>
                    </Space>
                  ) : (
                    <Space direction="vertical" size={4}>
                      <Text>No equipment has been enabled for PMV operations.</Text>
                      <Button type="link" onClick={() => navigate('/asset/assets')}>
                        Enable an Asset for PMV
                      </Button>
                    </Space>
                  )
                }
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
          scroll={{ x: 1540 }}
          size="middle"
        />
      </Space>
    </WorkspaceLayout>
  );
};

export default PmvRegisterPage;
